import React, { useState, useEffect, useCallback } from "react";
import { Check, ChevronLeft, Lock, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useReadOnlyGuard } from "@/hooks/useReadOnlyGuard";
import { WATER_QUICK_ADD_TOAST_ID, canQuickAddWater } from "@/lib/waterEvents";
import { FootstepsIcon } from "@/components/icons/FootstepsIcon";
import { WaterCupIcon } from "@/components/icons/WaterCupIcon";
import { FastingIcon } from "@/components/icons/FastingIcon";
import { useFasting } from "@/contexts/FastingContext";
import { useWaterTracking } from "@/contexts/WaterTrackingContext";
import { useSteps, useLiveStepsView } from "@/contexts/StepsContext";
import { useCountUp } from "@/hooks/useCountUp";
import { useChallenge } from "@/contexts/ChallengeContext";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import WaterTrackingContent from "../content/WaterTrackingContent";
import FastingContent from "../content/FastingContent";
import StepsContent from "../content/StepsContent";
import FeatureLockedMessage from "@/components/challenge/FeatureLockedMessage";
import MetricHistoryTeaser from "@/components/metrics/history/MetricHistoryTeaser";
import { FeatureKey } from "@/types/challenge";
import { useNavigate } from "react-router-dom";
import { metricHistoryPath, PROGRESS_PATH } from "@/lib/metricHistoryRoutes";
import { useHistoryOpened } from "@/hooks/useHistoryOpened";
import { useMetricsHistoryDoor } from "@/hooks/useMetricsHistoryDoor";
import { useMetricUsageSnapshot } from "@/hooks/useMetricUsageSnapshot";
import { METRIC_CHART_COLOR } from "@/components/metrics/charts/metricVisuals";
import { buildHistoryDoorProps, HISTORY_DOOR_EVENT, type MetricKey } from "@/lib/metricLogging";
import { logAction } from "@/services/eventLogService";
import { unitForNumeral } from "@/lib/metricCounters";
import { formatMetricNumber } from "@/lib/metricPeriods";

// מיפוי בין key של מטריקה ל-FeatureKey
const METRIC_TO_FEATURE: Record<string, FeatureKey> = {
  water: "water_tracking",
  fasting: "fasting_tracking",
  steps: "steps_tracking"
};

export const MetricsSection = React.memo(function MetricsSection() {
  const {
    isActive: isFastingActive,
    currentSeconds,
    /**
     * §4.1's rider, and the release that ships fasting history is the one it had
     * to be fixed in: this tile used to hard-code 16 hours.
     *
     * `FastingContext` already resolves the real number — the ACTIVE session's
     * `target_hours` first, then `user_fasting_settings.default_target_hours`,
     * then 16 as a last resort — so the fix is to stop ignoring it, not to add a
     * read. A member on a 14-hour protocol was shown a ring that could not fill
     * and a "completed" state she could not reach; one on 18 was told she was
     * done two hours early. Note there is no fasting column in
     * `user_metric_prefs`, so this is the only source at any layer.
     */
    targetHours: fastingGoalHours
  } = useFasting();
  const {
    cupsConsumed,
    maxCups,
    addCup,
    removeCup,
    isLoading: waterLoading,
    error: waterError
  } = useWaterTracking();
  const guard = useReadOnlyGuard();
  const {
    stepsCount,
    targetSteps,
    isHealthConnected
  } = useSteps();
  // Home shows the count, so Health is read every minute while it's up.
  useLiveStepsView();
  const displaySteps = useCountUp(stepsCount);
  const { isFeatureUnlocked, getFeatureUnlockDay } = useChallenge();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  /**
   * A metric whose history she asked for, held until the drawer has actually
   * closed. See `handleOpenMetricHistory` — this is not a nicety.
   */
  const [pendingHistory, setPendingHistory] = useState<MetricKey | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);
  const showHistoryDoor = useMetricsHistoryDoor();
  /**
   * ENABLED FOR THE DRAWER TOO, not only for the fake door.
   *
   * The snapshot used to ride the door's kill switch alone, which was right
   * when the door was the only thing that emitted. `history_opened` now emits
   * from the drawer — a surface that renders whether or not the flag is on — and
   * with the flag off the tap would have carried `usage_loaded: false` and three
   * nulls, i.e. exactly the properties that make it comparable to the Phase 0
   * read would have been the ones missing.
   *
   * Keyed on `drawerOpen` rather than mounted unconditionally so Home still
   * costs nothing until she opens a metric: the fetch starts on the tile tap,
   * and the teaser row is at least one more tap after that. If it has not landed
   * by then the honest `loaded: false` shape is what ships, which is what that
   * field is for.
   */
  const metricUsage = useMetricUsageSnapshot(showHistoryDoor || drawerOpen);

  /**
   * The drawer's Android-back entry, pushed once and popped once.
   *
   * `ownsHistoryEntry()` IS THE GUARD, and three real races need it. Double-tap a
   * tile and the second tap pushes a second dummy before the overlay mounts, so
   * one back press does nothing. Double-tap the close X and the second
   * `history.back()` pops a REAL entry, dropping her onto whatever preceded Home.
   * Tap the teaser after a hardware back has already popped the dummy, and the
   * `replace` below would replace HOME. Every push and every pop is now
   * conditional on the entry we are about to act on actually being ours.
   */
  const ownsHistoryEntry = () => window.history.state?.metricsDrawer === true;

  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (open && !ownsHistoryEntry()) {
      window.history.pushState({ metricsDrawer: true }, '');
    }
    setDrawerOpen(open);
  }, []);

  const closeDrawer = useCallback(() => {
    if (ownsHistoryEntry()) window.history.back();
    else setDrawerOpen(false);
  }, []);

  // The metric drawer is now the only sheet this section opens — ההתקדמות שלי
  // navigates to a route, where Android back is the platform's own and needs no
  // listener of ours. That is one of the reasons history became a route.
  useEffect(() => {
    if (!drawerOpen) return;

    const onPopState = () => setDrawerOpen(false);

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [drawerOpen]);

  const handleMetricClick = (metricKey: string) => {
    setSelectedMetric(metricKey);
    handleDrawerOpenChange(true);
  };

  /**
   * The teaser row's way out of the drawer and into `/history/:metric`.
   *
   * IT CLOSES THE DRAWER FIRST AND NAVIGATES IN THE EFFECT BELOW, which looks
   * like ceremony and is not. `vaul` puts `position: fixed` on `<body>` while a
   * drawer is open on Safari and restores it from an effect that runs when `open`
   * flips false — an effect with NO unmount cleanup (verified in
   * vaul/dist/index.mjs:820-843). Navigating straight out unmounts the drawer
   * while it is still open, so the restore never runs and the whole app is
   * scroll-locked until another drawer is opened and closed. It spares native
   * WKWebView, whose UA lacks the "Safari" token vaul tests for — and the two real
   * members who have ever used this door are both on web.
   *
   * `replace: true` in the effect, for the history stack: the dummy entry the
   * drawer pushed carries Home's own URL, so a plain push would leave [Home,
   * dummy, /history] and a member pressing back twice would appear to be stuck on
   * Home. Replacing it leaves [Home, /history].
   */
  const handleOpenMetricHistory = (metricKey: MetricKey) => {
    handleHistoryOpened(metricKey);
    setPendingHistory(metricKey);
    setDrawerOpen(false);
  };

  useEffect(() => {
    if (!pendingHistory || drawerOpen) return;
    // Child effects run before the parent's, so `vaul`'s restore has already run
    // by the time this does — that ordering is the whole point of the two-step.
    navigate(metricHistoryPath(pendingHistory), {
      replace: ownsHistoryEntry(),
      // `from: 'drawer'` is what tells the history screen its back arrow may pop:
      // the raw `pushState` above overwrote React Router's `idx`, so the screen
      // cannot tell from the history entry alone that Home is behind it.
      state: { from: 'drawer' },
    });
    setPendingHistory(null);
  }, [pendingHistory, drawerOpen, navigate]);
  const formatFastingTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
  const waterProgress = maxCups > 0 ? cupsConsumed / maxCups * 100 : 0;
  const stepsProgress = targetSteps > 0 ? stepsCount / targetSteps * 100 : 0;
  const waterCompleted = cupsConsumed >= maxCups && maxCups > 0;
  const stepsCompleted = stepsCount >= targetSteps && targetSteps > 0;
  /**
   * `> 0` for the same reason water and steps guard their goals two lines up:
   * a non-positive target makes `currentSeconds >= 0` true for every running
   * fast, so the tile would report "completed" the second she started. A
   * vacuous true is worse than no answer — this phase exists because numbers
   * lied.
   */
  const hasFastingGoal = Number.isFinite(fastingGoalHours) && fastingGoalHours > 0;
  const fastingGoalSeconds = fastingGoalHours * 3600;
  const fastingCompleted = isFastingActive && hasFastingGoal && currentSeconds >= fastingGoalSeconds;

  const isWaterUnlocked = isFeatureUnlocked("water_tracking");
  const isFastingUnlocked = isFeatureUnlocked("fasting_tracking");
  const isStepsUnlocked = isFeatureUnlocked("steps_tracking");

  /**
   * ההתקדמות שלי — a fake door until 2026-09-09, a real one since.
   *
   * The row is unchanged and deliberately plain; only what it opens changed. It
   * used to reveal one honest sentence saying history did not exist yet. It now
   * lists the three metrics, each opening that metric's history.
   *
   * IT NOW NAVIGATES, AND THE CHOOSER SHEET IS GONE. The sheet existed because
   * one row cannot open three drawers and every rule for picking a metric
   * resolves to steps — the one PASSIVE metric, whose rows the health sync
   * writes on app open — so a member who diligently logs water would have been
   * sent to her step count. `/progress` answers that without a middle layer: it
   * IS the three metrics, each with her own sentence and count, so the choice is
   * made in front of the data instead of in front of three bare labels.
   *
   * It also keeps Home's cost flat: the row issues no `metric_days` read, so
   * removing the week strip removed Home's only unconditional history query.
   *
   * The gate state still rides along as properties: `history_door_tapped` is
   * the Phase 0 baseline `history_opened` is compared against, and the two must
   * keep the same shape or the comparison is between two different populations.
   * Trackers are challenge-gated
   * (`isFeatureUnlocked` returns false with no `progress` at all), so the
   * addressable base may be hundreds rather than the ~11,200 profiles anyone
   * would assume. Taps split by `unlocked_count` answer that from the client
   * even if the SQL never gets run.
   */
  const doorProps = () => buildHistoryDoorProps(
    { waterUnlocked: isWaterUnlocked, fastingUnlocked: isFastingUnlocked, stepsUnlocked: isStepsUnlocked },
    metricUsage,
  );

  /**
   * One-tap cup from the Home tile, with undo. The loading check comes BEFORE
   * the read-only guard so a tap during load shows nothing at all: the context
   * upserts the whole count, and a tap before the first read resolves would
   * write 1 over her real number. One toast id for every tap, so the undo
   * takes back exactly one cup — the last — however fast she tapped.
   *
   * A failed read leaves the count at a stale 0 with `isLoading` false, so the
   * gate also refuses while the context reports an error (see canQuickAddWater).
   */
  const canQuickAdd = canQuickAddWater({ unlocked: isWaterUnlocked, loading: waterLoading, error: waterError });
  const handleQuickAddWater = () => {
    if (!canQuickAdd) return;
    if (guard()) return;
    addCup({ surface: 'home_tile' });
    toast('נוספה כוס', {
      id: WATER_QUICK_ADD_TOAST_ID,
      duration: 3000,
      action: {
        label: 'ביטול',
        onClick: () => removeCup({ surface: 'home_tile', undo: true }),
      },
      classNames: { actionButton: 'min-h-11 px-4' },
    });
  };

  const handleHistoryDoorTap = () => {
    logAction('feature', HISTORY_DOOR_EVENT, doorProps());
    navigate(PROGRESS_PATH);
  };

  /**
   * `history_opened`, through the shared hook rather than a second hand-rolled
   * call. This component used to build the event itself with its own copy of the
   * gate-state literal — which is exactly the drift `useHistoryOpened` exists to
   * prevent, sitting in the file that most needed it. The event's shape is the
   * comparison the whole feature's go/no-go rests on: `history_door_tapped` is
   * the Phase 0 baseline, and two entrances emitting different property sets
   * describe two different populations.
   *
   * It is an ACT, fired from an `onClick`, and never from a render or an effect.
   */
  const handleHistoryOpened = useHistoryOpened(showHistoryDoor || drawerOpen);

  const metrics = [{
    key: "water",
    featureKey: "water_tracking" as FeatureKey,
    icon: <WaterCupIcon className="h-5 w-5" />,
    label: "שתיית מים",
    status: "צפייה",
    value: `${cupsConsumed}`,
    progress: waterProgress,
    path: '/water-tracking',
    completed: waterCompleted,
    overGoal: waterProgress > 100,
    isUnlocked: isWaterUnlocked
  }, {
    key: "fasting",
    featureKey: "fasting_tracking" as FeatureKey,
    icon: <FastingIcon className="h-5 w-5" />,
    label: "צום",
    status: isFastingActive ? "פעיל" : "לא פעיל",
    // "0:00" read as a stopped timer; not fasting is a state, not a zero.
    value: isFastingActive ? formatFastingTime(currentSeconds) : "לא בצום",
    progress: isFastingActive && hasFastingGoal
      ? Math.min(100, currentSeconds / fastingGoalSeconds * 100)
      : 0,
    path: '/fasting',
    completed: fastingCompleted,
    overGoal: hasFastingGoal && currentSeconds > fastingGoalSeconds,
    isUnlocked: isFastingUnlocked
  }, {
    key: "steps",
    featureKey: "steps_tracking" as FeatureKey,
    icon: <FootstepsIcon className="h-5 w-5" />,
    label: "כמות צעדים",
    status: "צפייה",
    value: `${displaySteps.toLocaleString('he-IL')}`,
    progress: stepsProgress,
    path: '/steps',
    completed: stepsCompleted,
    overGoal: stepsProgress > 100,
    isUnlocked: isStepsUnlocked
  }];
  /**
   * The goal under each number (owner-approved "glass", canvas board 7): a bare
   * "5" said nothing about how far there was to go. Null where there is no
   * honest goal to show — no target set, or no fast running.
   */
  const fastingGoalNumeral = formatMetricNumber('fasting', fastingGoalHours);
  const tileCaption: Record<string, string | null> = {
    water: maxCups > 0 ? `מתוך ${maxCups} ${unitForNumeral('water', String(maxCups))}` : null,
    fasting: isFastingActive && hasFastingGoal
      ? `מתוך ${fastingGoalNumeral} ${unitForNumeral('fasting', fastingGoalNumeral)}`
      : null,
    steps: targetSteps > 0 ? `מתוך ${targetSteps.toLocaleString('he-IL')}` : null,
  };
  const currentMetric = metrics.find(m => m.key === selectedMetric);
  return <>
      <section className="px-0 py-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">המעקב היומי שלי</h2>
        </div>
        
        {/* כפתורי ניווט גדולים */}
        <div className="flex gap-2 w-full max-w-md mx-auto">
          {metrics.map(metric => {
          /*
            THE PALETTE COMES FROM `metricVisuals` NOW — one table for the app.

            These tiles used to carry their own: water `text-info`, fasting
            `text-primary`, steps `text-success`. Two of the three therefore
            CHANGED COLOUR one tap later, because the history surfaces read the
            chart table where fasting is purple and steps was orange. Three
            reviewers found it independently.

            Resolving it cost fasting its brand orange, deliberately: `--primary`
            is the colour of HER PROGRESS and of the screen's one real CTA, and a
            metric wearing it makes it mean "this app" instead. It also failed
            2.45:1 as a fill. `--success` went with it for the matching reason —
            a status colour must not double as a series colour. Same hues either
            side of the tap, from one place, all three contrast-checked.
          */
          const isLocked = !metric.isUnlocked;
          const metricColor = METRIC_CHART_COLOR[metric.key as MetricKey];
          const clampedProgress = isLocked ? 0 : Math.min(100, metric.progress);
          const caption = isLocked ? null : tileCaption[metric.key];
          const fastingIdle = metric.key === 'fasting' && !isFastingActive;
          const reached = !isLocked && metric.completed;
          // The wrapper owns the tile's box so the quick-add "+" can sit beside
          // the tile button as a SIBLING (a button cannot nest in a button).
          return <div key={metric.key} className="relative flex-1 aspect-square">
              <button onClick={() => handleMetricClick(metric.key)}
                /*
                  The tile is an icon and a bare number — "0:00", "8" — with no
                  text label anywhere on it, so a screen reader announced three
                  unlabeled buttons on the app's busiest screen. IS 5568 is a
                  legal floor here, not a nicety. `aria-label` and not a visible
                  label: the tiles are deliberately compact, and adding words
                  would cost the number its size.
                */
                aria-label={isLocked
                  ? `${metric.label} — נעול`
                  : [metric.label, caption ? `${metric.value} ${caption}` : metric.value, reached ? 'היעד הושג' : null]
                      .filter(Boolean).join(', ')}
                /*
                  `overflow-hidden` moved OFF the tile and onto the fill below it,
                  which is the only thing it was ever for. On an `aspect-square`
                  whose width cannot grow, it silently clipped the value at large
                  Dynamic Type — the one setting this app's audience is most
                  likely to have turned up.
                */
                className={`h-full w-full rounded-2xl bg-card shadow-sm flex flex-col items-center justify-center gap-1.5 py-3 px-2 active:scale-[0.98] transition-transform duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--primary-strong))] focus-visible:outline-offset-2 relative ${isLocked ? 'opacity-60' : ''}`}>
                {/* מילוי מלמטה למעלה */}
                {/*
                  A CLIPPING WRAPPER, not `overflow-hidden` on the fill itself.
                  The tile used to carry it, which also clipped the VALUE at large
                  Dynamic Type; moving it onto the fill fixed that and introduced a
                  new one, because a fill clips its children and not itself — at
                  100% its square top corners bled past the tile's rounded top.
                  A wrapper pinned to the tile's own radius clips the fill and
                  leaves the icon and the number, which are its siblings, alone.
                */}
                <div aria-hidden="true" className="absolute inset-0 overflow-hidden rounded-2xl">
                  {/* The level line is what makes it read as a glass filling:
                      a faint fill alone left 90% and 100% looking the same. */}
                  <div
                    className="absolute inset-x-0 bottom-0 transition-[height] duration-300"
                    style={{
                      height: `${clampedProgress}%`,
                      backgroundColor: isLocked ? undefined : `${metricColor}${reached ? '33' : '2E'}`,
                      borderTop: clampedProgress > 0 && clampedProgress < 100 ? `3px solid ${metricColor}` : undefined,
                    }}
                  />
                </div>

                {reached && (
                  <span
                    aria-hidden="true"
                    data-goal-reached
                    className="absolute top-2 start-2 z-10 flex h-5 w-5 items-center justify-center rounded-full"
                    style={{ backgroundColor: metricColor }}
                  >
                    <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
                  </span>
                )}

                {/* אייקון המטריקה עם באדג' מנעול */}
                <div
                  className="relative z-10 text-muted-foreground"
                  style={{ color: isLocked ? undefined : metricColor }}
                >
                  {React.cloneElement(metric.icon as React.ReactElement, {
                    className: "h-7 w-7",
                    strokeWidth: 1.5
                  })}
                  {/* באדג' מנעול קטן */}
                  {isLocked && (
                    <div className="absolute -bottom-1 -end-1 bg-muted rounded-full p-0.5">
                      <Lock className="h-3 w-3 text-muted-foreground" strokeWidth={2.5} />
                    </div>
                  )}
                </div>
                
                {/* ערכים או טקסט נעול */}
                <div
                  data-private
                  className={`relative z-10 ${fastingIdle && !isLocked ? 'text-sm font-medium text-muted-foreground' : 'text-base font-semibold'} ${isLocked ? 'text-muted-foreground' : fastingIdle ? '' : 'text-foreground'}`}
                >
                  {isLocked ? "נעול" : metric.value}
                </div>
                {caption && (
                  <div data-tile-caption className="relative z-10 text-xs leading-tight text-muted-foreground">
                    {caption}
                  </div>
                )}
              </button>
              {/* Opposite corner from the goal-reached badge (top-2 start-2):
                  in RTL `start` is the right edge and `end` the left. The
                  visible chip is `h-6 w-6` (27px at the 18px root), pinned into the top-end corner so it
                  clears the centred icon on a 360px screen; the button keeps
                  a hit area of 35 x 44px anchored to that corner
                  (`items-start` = top, `justify-end` = inline end). Fixed
                  pixels on purpose (the root font size is 18px), and 35px
                  wide on purpose: on a 360px screen the icon starts 35.25px
                  from the tile's edge, so anything wider takes taps meant
                  for the icon (which opens the drawer). Padding only on the
                  corner's two sides, so the 27px chip still fits unshrunk.
                  A sibling, so a tap here never opens the drawer; disabled
                  while water data loads, which swallows the tap. */}
              {metric.key === 'water' && !isLocked && (
                <button
                  type="button"
                  onClick={handleQuickAddWater}
                  disabled={!canQuickAdd}
                  aria-label="הוספת כוס מים"
                  className="group absolute top-0 end-0 z-20 flex h-[44px] w-[35px] items-start justify-end pe-1.5 pt-1.5 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--primary-strong))] focus-visible:outline-offset-2 rounded-full"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background shadow-sm transition-transform duration-150 group-active:scale-90">
                    <Plus className="h-4 w-4 text-foreground" strokeWidth={2.5} />
                  </span>
                </button>
              )}
            </div>;
        })}
        </div>

        {/*
          THE SUNDAY RECAP CARD IS GONE, AND WITH IT THE LAST CALENDAR ON HOME.

          It rendered `שבוע חדש מתחיל` plus a count of last week's logged days
          for three days out of seven. Two things were wrong with it and only
          one was cosmetic. The owner's reading — "no need for the week begins
          bar" — is the cosmetic half: a banner that appears on Sunday, changes
          nothing, and disappears on Wednesday is chrome that moves.

          The structural half is that a WEEK is a calendar window, and this
          feature's one rule is that nothing may render, count or imply a day
          she did not log. The card was careful about that (no denominator, no
          zero, no comparison) but it was careful about a shape that did not
          need to exist: at a measured 2-3 logged days a fortnight, "last week"
          is a window that is usually empty, so the card's majority behaviour
          was to render nothing at all.

          ההתקדמות שלי, directly below, is the surface that answers the same
          question without a window on it. `WeeklyRecapCard`, its test, and
          `metricHomeHistory`'s window helper are deleted rather than hidden.
        */}

        {showHistoryDoor && (
          <div className="w-full max-w-md mx-auto mt-3">
            <button
              onClick={handleHistoryDoorTap}
              className="w-full min-h-[52px] flex items-center justify-between gap-3 rounded-2xl bg-card shadow-sm px-4 py-3 active:scale-[0.98] active:bg-muted-warm transition-transform duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--primary-strong))] focus-visible:outline-offset-2"
            >
              <span className="text-base font-medium text-foreground">ההתקדמות שלי</span>
              {/* Visually left = "forward" in RTL, so a left chevron is the
                  advance affordance here, not a back one. */}
              <ChevronLeft className="h-5 w-5 text-muted-foreground shrink-0" strokeWidth={2} />
            </button>
          </div>
        )}
      </section>

      <Drawer open={drawerOpen} onOpenChange={(open) => {
        // Swipe-down or overlay tap. `closeDrawer` pops the entry we pushed only
        // if it is still ours — a hardware back may already have popped it.
        if (!open && drawerOpen) closeDrawer();
      }}>
        <DrawerContent
          className="h-[90vh]"
          dir="rtl"
          // Radix catches Escape on the document in the CAPTURE phase, before an
          // inline editor's own handler runs, so Escape meant "close the drawer"
          // even while cancelling an edit. A field marked data-escape-cancels keeps
          // Escape for itself.
          onEscapeKeyDown={(e) => {
            if ((e.target as HTMLElement | null)?.closest?.('[data-escape-cancels]')) e.preventDefault();
          }}
        >
          <DrawerHeader className="relative pt-2 pb-4 px-8">
            <button onClick={closeDrawer} className="absolute end-8 top-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full active:bg-muted transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--primary-strong))] focus-visible:outline-offset-2" aria-label="סגירה">
              <X className="h-5 w-5" />
            </button>
            {/* `text-start`, not `text-right`. Under dir="rtl" they resolve to the
                same side today, but the physical property is the one that silently
                stops being true — this feature already shipped a screen ragged
                against the wrong margin by confusing the two. */}
            <DrawerTitle className="text-xl font-semibold text-start">
              {currentMetric?.label}
            </DrawerTitle>
            {/* Radix requires a description or an explicit opt-out on every
                DialogContent, and warned 11x without one. `sr-only` rather than
                `aria-describedby={undefined}`: a screen-reader user opening this
                drawer should be told what it holds, and IS 5568 is a legal
                baseline here, not a nicety. */}
            <DrawerDescription className="sr-only">
              המעקב היומי וההיסטוריה של {currentMetric?.label}
            </DrawerDescription>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto px-8 pb-6">
            {/*
              THE LOCK IS THE CONTENT, THE TEASER IS ITS SIBLING (D2).

              A challenge-locked metric renders `FeatureLockedMessage` instead of
              the tracker — that gate governs first-time unlock of LOGGING, which
              is all it was ever for. History is reachable "regardless of
              subscription, CHALLENGE ENROLMENT, or course-only status", so the
              teaser row sits OUTSIDE that branch: a member between challenges,
              and an Apple reviewer on an expired demo account, still reach what
              they recorded. Nesting the row inside the unlocked branch would
              re-create the exact defect the retired tab shell was built to fix.

              The tab strip that used to wrap this is gone. It made "today" a
              named destination when the drawer already was one, and it cost ~72px
              of a 90vh sheet to say so.
            */}
            {selectedMetric && (
              <>
                {currentMetric?.isUnlocked ? (
                  <>
                    {selectedMetric === 'water' && <WaterTrackingContent />}
                    {selectedMetric === 'fasting' && <FastingContent />}
                    {selectedMetric === 'steps' && <StepsContent />}
                  </>
                ) : (
                  <FeatureLockedMessage
                    feature={METRIC_TO_FEATURE[selectedMetric]}
                    unlockDay={getFeatureUnlockDay(METRIC_TO_FEATURE[selectedMetric])}
                  />
                )}

                {/* LAST, and below the logging content — never above it. She
                    opened this drawer to log; an entry to a different screen
                    placed first would compete with the thing she came for. */}
                <MetricHistoryTeaser
                  metric={selectedMetric as MetricKey}
                  onOpen={() => handleOpenMetricHistory(selectedMetric as MetricKey)}
                  className="mt-6"
                />
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>;
});
